import React, { useState, useEffect } from 'react';
import { API_CONFIG } from '../../../config/api';
import { Search, Plus, Edit, Trash2, Eye, EyeOff, Globe, User, Shield, Monitor, Wifi } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import toast from 'react-hot-toast';

interface SessionProfile {
  id: string;
  profile_name: string;
  platform: string;
  username?: string;
  description?: string;
  profile_path?: string;
  user_agent?: string;
  viewport_size: any;
  proxy_config?: any;
  headers?: any;
  is_active: boolean;
  last_used_at?: string;
  created_at: string;
  updated_at: string;
}

interface FormData {
  profile_name: string;
  platform: string;
  username: string;
  description: string;
  profile_path: string;
  user_agent: string;
  viewport_width: number;
  viewport_height: number;
  proxy_enabled: boolean;
  proxy_host: string;
  proxy_port: string;
  proxy_username: string;
  proxy_password: string;
  custom_headers: string;
  is_active: boolean;
}

const ScrapeSessionManagement: React.FC = () => {
  const [profiles, setProfiles] = useState<SessionProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [platformFilter, setPlatformFilter] = useState('all');
  const [activeFilter, setActiveFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editingProfile, setEditingProfile] = useState<SessionProfile | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    profile_name: '',
    platform: 'general',
    username: '',
    description: '',
    profile_path: '',
    user_agent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/120.0.0.0 Safari/537.36',
    viewport_width: 1920,
    viewport_height: 1080,
    proxy_enabled: false,
    proxy_host: '',
    proxy_port: '',
    proxy_username: '',
    proxy_password: '',
    custom_headers: '{}',
    is_active: true
  });

  const platforms = [
    { value: 'general', label: '通用', icon: Globe, color: 'gray' },
    { value: 'social', label: '社交媒体', icon: User, color: 'blue' },
    { value: 'ecommerce', label: '电商平台', icon: Shield, color: 'green' },
    { value: 'news', label: '新闻媒体', icon: Monitor, color: 'purple' },
    { value: 'custom', label: '自定义', icon: Wifi, color: 'orange' }
  ];

  useEffect(() => {
    fetchProfiles();
  }, []);

  const fetchProfiles = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/scrape-admin-manage?action=session_list`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          page: 1,
          limit: 1000
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result.success) {
        setProfiles(result.data.data || []);
      } else {
        console.error('获取会话配置失败:', result.error);
        toast.error('获取会话配置失败');
      }
    } catch (error) {
      console.error('获取会话配置失败:', error);
      toast.error('获取会话配置失败');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;

    try {
      setSubmitting(true);
      
      // 验证JSON格式
      let customHeaders;
      try {
        customHeaders = JSON.parse(formData.custom_headers);
      } catch (error) {
        toast.error('自定义请求头不是有效的JSON格式');
        return;
      }

      const requestData = {
        profile_name: formData.profile_name,
        platform: formData.platform,
        username: formData.username || null,
        description: formData.description || null,
        profile_path: formData.profile_path || null,
        user_agent: formData.user_agent,
        viewport_size: {
          width: formData.viewport_width,
          height: formData.viewport_height
        },
        proxy_config: formData.proxy_enabled ? {
          host: formData.proxy_host,
          port: parseInt(formData.proxy_port),
          username: formData.proxy_username || null,
          password: formData.proxy_password || null
        } : null,
        headers: Object.keys(customHeaders).length > 0 ? customHeaders : null,
        is_active: formData.is_active
      };

      if (editingProfile) {
        // 更新配置
        const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/scrape-admin-manage?action=session_update`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            id: editingProfile.id,
            ...requestData
          })
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        if (!result.success) {
          throw new Error(result.error);
        }

        toast.success('会话配置更新成功');
      } else {
        // 创建配置
        const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/scrape-admin-manage?action=session_create`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(requestData)
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        if (!result.success) {
          throw new Error(result.error);
        }

        toast.success('会话配置创建成功');
      }

      await fetchProfiles();
      setShowForm(false);
      setEditingProfile(null);
      resetForm();
    } catch (error) {
      console.error('保存会话配置失败:', error);
      toast.error('保存失败：' + (error as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`确定要删除会话配置「${name}」吗？`)) return;

    try {
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/scrape-admin-manage?action=session_delete&id=${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error);
      }

      toast.success('会话配置删除成功');
      await fetchProfiles();
    } catch (error) {
      console.error('删除会话配置失败:', error);
      toast.error('删除失败：' + (error as Error).message);
    }
  };

  const handleEdit = (profile: SessionProfile) => {
    setEditingProfile(profile);
    setFormData({
      profile_name: profile.profile_name,
      platform: profile.platform,
      username: profile.username || '',
      description: profile.description || '',
      profile_path: profile.profile_path || '',
      user_agent: profile.user_agent || '',
      viewport_width: profile.viewport_size?.width || 1920,
      viewport_height: profile.viewport_size?.height || 1080,
      proxy_enabled: !!profile.proxy_config,
      proxy_host: profile.proxy_config?.host || '',
      proxy_port: profile.proxy_config?.port?.toString() || '',
      proxy_username: profile.proxy_config?.username || '',
      proxy_password: profile.proxy_config?.password || '',
      custom_headers: profile.headers ? JSON.stringify(profile.headers, null, 2) : '{}',
      is_active: profile.is_active
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      profile_name: '',
      platform: 'general',
      username: '',
      description: '',
      profile_path: '',
      user_agent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/120.0.0.0 Safari/537.36',
      viewport_width: 1920,
      viewport_height: 1080,
      proxy_enabled: false,
      proxy_host: '',
      proxy_port: '',
      proxy_username: '',
      proxy_password: '',
      custom_headers: '{}',
      is_active: true
    });
  };

  const filteredProfiles = profiles.filter(profile => {
    const matchesSearch = profile.profile_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (profile.description || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         profile.platform.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesPlatform = platformFilter === 'all' || profile.platform === platformFilter;
    const matchesActive = activeFilter === 'all' || 
                         (activeFilter === 'active' && profile.is_active) ||
                         (activeFilter === 'inactive' && !profile.is_active);
    return matchesSearch && matchesPlatform && matchesActive;
  });

  const getPlatformInfo = (platform: string) => {
    return platforms.find(p => p.value === platform) || platforms[0];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center">
              <Shield className="w-6 h-6 mr-2 text-blue-600" />
              会话配置管理
            </h2>
            <button
              onClick={() => {
                setEditingProfile(null);
                resetForm();
                setShowForm(true);
              }}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              <Plus className="w-4 h-4 mr-2" />
              添加配置
            </button>
          </div>
        </div>
        
        <div className="p-6">
          {/* 搜索和筛选 */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="搜索配置名称、描述或平台..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <select
              value={platformFilter}
              onChange={(e) => setPlatformFilter(e.target.value)}
              className="w-full sm:w-48 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
            >
              <option value="all">全部平台</option>
              {platforms.map(platform => (
                <option key={platform.value} value={platform.value}>
                  {platform.label}
                </option>
              ))}
            </select>
            <select
              value={activeFilter}
              onChange={(e) => setActiveFilter(e.target.value)}
              className="w-full sm:w-48 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
            >
              <option value="all">全部状态</option>
              <option value="active">已启用</option>
              <option value="inactive">已禁用</option>
            </select>
          </div>

          {/* 配置列表 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {filteredProfiles.map((profile) => {
              const platformInfo = getPlatformInfo(profile.platform);
              const PlatformIcon = platformInfo.icon;
              
              return (
                <div key={profile.id} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className={`p-2 rounded-lg bg-${platformInfo.color}-100`}>
                        <PlatformIcon className={`w-5 h-5 text-${platformInfo.color}-600`} />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{profile.profile_name}</h3>
                        <div className="flex items-center space-x-2 mt-1">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${platformInfo.color}-100 text-${platformInfo.color}-800`}>
                            {platformInfo.label}
                          </span>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            profile.is_active 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {profile.is_active ? (
                              <><Eye className="w-3 h-3 mr-1" />启用</>
                            ) : (
                              <><EyeOff className="w-3 h-3 mr-1" />禁用</>
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleEdit(profile)}
                        className="text-blue-600 hover:text-blue-900"
                        title="编辑"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(profile.id, profile.profile_name)}
                        className="text-red-600 hover:text-red-900"
                        title="删除"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {profile.description && (
                    <p className="text-gray-600 text-sm mb-3">{profile.description}</p>
                  )}

                  <div className="space-y-2 mb-4">
                    {profile.username && (
                      <div className="flex items-center text-sm text-gray-600">
                        <User className="w-4 h-4 mr-2" />
                        用户名: {profile.username}
                      </div>
                    )}
                    
                    <div className="flex items-center text-sm text-gray-600">
                      <Monitor className="w-4 h-4 mr-2" />
                      视窗: {profile.viewport_size?.width || 1920} × {profile.viewport_size?.height || 1080}
                    </div>

                    {profile.proxy_config && (
                      <div className="flex items-center text-sm text-gray-600">
                        <Wifi className="w-4 h-4 mr-2" />
                        代理: {profile.proxy_config.host}:{profile.proxy_config.port}
                      </div>
                    )}
                  </div>

                  {profile.user_agent && (
                    <div className="bg-gray-50 rounded-md p-3 mb-4">
                      <div className="text-xs text-gray-500 mb-1">User Agent</div>
                      <div className="text-sm text-gray-700 truncate">
                        {profile.user_agent}
                      </div>
                    </div>
                  )}

                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>创建时间: {new Date(profile.created_at).toLocaleDateString()}</span>
                    {profile.last_used_at && (
                      <span>最后使用: {new Date(profile.last_used_at).toLocaleDateString()}</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {filteredProfiles.length === 0 && (
            <div className="text-center py-12">
              <Shield className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">暂无会话配置数据</p>
            </div>
          )}
        </div>
      </div>

      {/* 表单弹窗 */}
      {showForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingProfile ? '编辑会话配置' : '添加会话配置'}
              </h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      配置名称 *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.profile_name}
                      onChange={(e) => setFormData({ ...formData, profile_name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="请输入配置名称"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      平台类型
                    </label>
                    <select
                      value={formData.platform}
                      onChange={(e) => setFormData({ ...formData, platform: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                    >
                      {platforms.map(platform => (
                        <option key={platform.value} value={platform.value}>
                          {platform.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      用户名
                    </label>
                    <input
                      type="text"
                      value={formData.username}
                      onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="可选，用于标识"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      配置文件路径
                    </label>
                    <input
                      type="text"
                      value={formData.profile_path}
                      onChange={(e) => setFormData({ ...formData, profile_path: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Chrome配置文件路径"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    描述
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="请输入配置描述"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    User Agent
                  </label>
                  <textarea
                    value={formData.user_agent}
                    onChange={(e) => setFormData({ ...formData, user_agent: e.target.value })}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="浏览器用户代理字符串"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      视窗宽度
                    </label>
                    <input
                      type="number"
                      min="800"
                      max="3840"
                      value={formData.viewport_width}
                      onChange={(e) => setFormData({ ...formData, viewport_width: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      视窗高度
                    </label>
                    <input
                      type="number"
                      min="600"
                      max="2160"
                      value={formData.viewport_height}
                      onChange={(e) => setFormData({ ...formData, viewport_height: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                <div className="border-t pt-4">
                  <label className="flex items-center mb-4">
                    <input
                      type="checkbox"
                      checked={formData.proxy_enabled}
                      onChange={(e) => setFormData({ ...formData, proxy_enabled: e.target.checked })}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm font-medium text-gray-700">启用代理</span>
                  </label>

                  {formData.proxy_enabled && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          代理主机
                        </label>
                        <input
                          type="text"
                          value={formData.proxy_host}
                          onChange={(e) => setFormData({ ...formData, proxy_host: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="127.0.0.1"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          代理端口
                        </label>
                        <input
                          type="text"
                          value={formData.proxy_port}
                          onChange={(e) => setFormData({ ...formData, proxy_port: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="8080"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          代理用户名
                        </label>
                        <input
                          type="text"
                          value={formData.proxy_username}
                          onChange={(e) => setFormData({ ...formData, proxy_username: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="可选"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          代理密码
                        </label>
                        <input
                          type="password"
                          value={formData.proxy_password}
                          onChange={(e) => setFormData({ ...formData, proxy_password: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="可选"
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    自定义请求头 (JSON格式)
                  </label>
                  <textarea
                    value={formData.custom_headers}
                    onChange={(e) => setFormData({ ...formData, custom_headers: e.target.value })}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                    placeholder='{"Accept-Language": "zh-CN,zh;q=0.9"}'
                  />
                </div>

                <div className="flex items-center">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">启用此配置</span>
                  </label>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowForm(false);
                      setEditingProfile(null);
                      resetForm();
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  >
                    取消
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
                  >
                    {submitting ? '保存中...' : '保存'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScrapeSessionManagement;